import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Zap, ArrowLeft, CreditCard, XCircle, RotateCcw } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

// Détection automatique du domaine pour la production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'boost-strategia.com' || window.location.hostname === 'www.boost-strategia.com') {
      return 'https://boost-strategia.com';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API = `${getApiUrl()}/api`;

const Profile = () => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [extraStudies, setExtraStudies] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelStep, setCancelStep] = useState('offer');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token) {
      navigate('/auth');
      return;
    }

    if (userData) {
      setUser(JSON.parse(userData));
    }

    fetchSubscription();
  }, [navigate]);

  const fetchSubscription = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/subscriptions/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  const handleBuyCredits = async () => {
    if (extraStudies < 1) {
      toast.error('Veuillez entrer un nombre valide');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          extra_studies: extraStudies,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Erreur lors de la création de la session de paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRetentionOffer = async () => {
    setCancelling(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/subscription/retention-offer`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setShowCancelDialog(false);
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'activation de la réduction");
    } finally {
      setCancelling(false);
    }
  };

  const openCancelDialog = () => {
    setCancelStep(subscription?.retention_offer_available ? 'offer' : 'confirm');
    setShowCancelDialog(true);
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(
        `${API}/subscription/cancel`,
        { reason: cancelReason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowCancelDialog(false);
      setCancelReason('');
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setCancelling(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/subscription/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réactivation');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/dashboard" data-testid="back-link">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au dashboard
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
              <Zap className="w-6 h-6 text-black" />
            </div>
            <span className="text-2xl font-heading font-bold">Boost-StrategIA</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-heading font-bold mb-8" data-testid="profile-title">Mon Profil</h1>

        {/* User Info */}
        <Card className="p-6 border-border mb-6" data-testid="user-info-card">
          <h2 className="text-xl font-heading font-semibold mb-4">Informations</h2>
          <div className="space-y-3">
            <div>
              <div className="text-sm text-muted">Nom</div>
              <div className="text-lg" data-testid="user-name">{user?.name}</div>
            </div>
            <div>
              <div className="text-sm text-muted">Email</div>
              <div className="text-lg" data-testid="user-email">{user?.email}</div>
            </div>
          </div>
        </Card>

        {/* Subscription Info */}
        <Card className="p-6 border-border mb-6" data-testid="subscription-card">
          <h2 className="text-xl font-heading font-semibold mb-4">Abonnement</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm text-muted">Plan actuel</div>
                <div className="text-lg capitalize" data-testid="current-plan">
                  {subscription?.plan || 'Gratuit'}
                </div>
              </div>
              <Link to="/pricing">
                <Button variant="outline" data-testid="change-plan-button">Changer de plan</Button>
              </Link>
            </div>

            {/* Annulation / reprise d'abonnement */}
            {subscription?.cancel_at_period_end ? (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-3" data-testid="cancellation-notice">
                <p className="text-sm text-amber-400">
                  Votre abonnement est annulé. Vous conservez l'accès jusqu'au{' '}
                  <strong>
                    {subscription.access_until
                      ? new Date(subscription.access_until).toLocaleDateString('fr-FR')
                      : 'la fin de la période payée'}
                  </strong>. Aucun nouveau prélèvement ne sera effectué.
                </p>
                <Button
                  onClick={handleReactivateSubscription}
                  disabled={cancelling}
                  className="bg-primary text-black"
                  data-testid="reactivate-subscription-button"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {cancelling ? 'Traitement...' : 'Reprendre mon abonnement'}
                </Button>
              </div>
            ) : subscription?.can_cancel ? (
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  onClick={openCancelDialog}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-sm"
                  data-testid="cancel-subscription-button"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Annuler mon abonnement
                </Button>
              </div>
            ) : null}

            <div>
              <div className="text-sm text-muted">Études restantes</div>
              <div className="text-2xl font-heading font-bold text-primary" data-testid="studies-remaining">
                {subscription?.studies_remaining || 0}
              </div>
            </div>

            {subscription?.billing_cycle && (
              <div>
                <div className="text-sm text-muted">Cycle de facturation</div>
                <div className="text-lg capitalize" data-testid="billing-cycle">
                  {subscription.billing_cycle === 'monthly' ? 'Mensuel' : 'Annuel'}
                </div>
              </div>
            )}

            {subscription?.expires_at && (
              <div>
                <div className="text-sm text-muted">Expire le</div>
                <div className="text-lg" data-testid="expiry-date">
                  {new Date(subscription.expires_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Buy Extra Studies */}
        <Card className="p-6 border-border" data-testid="buy-credits-card">
          <h2 className="text-xl font-heading font-semibold mb-4">
            <CreditCard className="w-5 h-5 inline mr-2" />
            Acheter des études supplémentaires
          </h2>
          <p className="text-muted mb-6">
            Besoin de plus d'études ? Achetez-en à la carte pour 29,99€ l'unité.
          </p>

          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm text-muted block mb-2">Nombre d'études</label>
              <Input
                type="number"
                min="1"
                value={extraStudies}
                onChange={(e) => setExtraStudies(parseInt(e.target.value) || 1)}
                className="w-full"
                data-testid="extra-studies-input"
              />
            </div>
            <div className="flex-1">
              <div className="text-sm text-muted mb-2">Total</div>
              <div className="text-2xl font-heading font-bold" data-testid="total-price">
                {(extraStudies * 29.99).toFixed(2)}€
              </div>
            </div>
            <Button
              onClick={handleBuyCredits}
              disabled={loading}
              className="shadow-glow-md"
              data-testid="buy-credits-button"
            >
              {loading ? 'Chargement...' : 'Acheter'}
            </Button>
          </div>
        </Card>
      </main>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent data-testid="cancel-subscription-dialog">
          {cancelStep === 'offer' ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-primary">
                  Attendez ! Voici -40 % sur vos 3 prochains mois
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Avant de partir, nous vous offrons <strong>40 % de réduction pendant 3 mois</strong> sur
                  votre abonnement {subscription?.plan}. La remise est appliquée automatiquement sur vos
                  prochaines factures, rien d'autre à faire. Offre valable une seule fois.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setCancelStep('confirm')}
                  className="text-muted hover:text-foreground"
                  data-testid="decline-retention-offer"
                >
                  Non merci, annuler quand même
                </Button>
                <Button
                  onClick={handleAcceptRetentionOffer}
                  disabled={cancelling}
                  className="bg-primary text-black shadow-glow-md"
                  data-testid="accept-retention-offer"
                >
                  {cancelling ? 'Activation...' : "J'accepte les -40 %"}
                </Button>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Annuler votre abonnement ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Vous conservez l'accès à la plateforme et à vos études jusqu'à la fin de la période
                  déjà payée. Aucun nouveau prélèvement ne sera effectué. Vous pourrez reprendre votre
                  abonnement à tout moment avant cette date.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-2">
                <label className="text-sm text-muted">Pouvez-vous nous dire pourquoi ? (optionnel)</label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Votre retour nous aide à améliorer Boost-StrategIA"
                  data-testid="cancel-reason-input"
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-dialog-dismiss">Garder mon abonnement</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); handleCancelSubscription(); }}
                  disabled={cancelling}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="confirm-cancel-subscription"
                >
                  {cancelling ? 'Annulation...' : 'Confirmer l\'annulation'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Profile;